import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

export type DialogTone = "default" | "danger" | "warning" | "success";

export type DialogOptions = {
  title: string;
  description?: string;
  content?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
  /** Override the default tone icon. */
  icon?: ReactNode;
  /** Set to true for one-button dismissible alerts. */
  alert?: boolean;
};

type PendingDialog = DialogOptions & {
  resolve: (value: boolean) => void;
};

type DialogContextValue = {
  confirm: (options: DialogOptions) => Promise<boolean>;
  alert: (options: Omit<DialogOptions, "alert">) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function toneIcon(tone: DialogTone, custom?: ReactNode) {
  if (custom) return custom;
  switch (tone) {
    case "danger":
      return <XCircle className="h-6 w-6 text-rose-600" />;
    case "warning":
      return <AlertTriangle className="h-6 w-6 text-amber-600" />;
    case "success":
      return <CheckCircle2 className="h-6 w-6 text-emerald-600" />;
    default:
      return <Info className="h-6 w-6 text-blue-600" />;
  }
}

function toneButtonClasses(tone: DialogTone) {
  switch (tone) {
    case "danger":
      return "bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500";
    case "warning":
      return "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500";
    case "success":
      return "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500";
    default:
      return "bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500";
  }
}

function toneRingClasses(tone: DialogTone) {
  switch (tone) {
    case "danger":
      return "ring-rose-100";
    case "warning":
      return "ring-amber-100";
    case "success":
      return "ring-emerald-100";
    default:
      return "ring-blue-100";
  }
}

const defaultConfirm = async (options: DialogOptions): Promise<boolean> => {
  const message = [options.title, options.description].filter(Boolean).join("\n\n");
  return window.confirm(message);
};

export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    return {
      confirm: defaultConfirm,
      alert: defaultConfirm,
    };
  }
  return context;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<PendingDialog | null>(null);

  const confirm = useCallback((options: DialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialog({ ...options, alert: false, resolve });
    });
  }, []);

  const alert = useCallback((options: Omit<DialogOptions, "alert">): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialog({ ...options, alert: true, resolve });
    });
  }, []);

  const close = useCallback(
    (value: boolean) => {
      setDialog((current) => {
        if (current) {
          current.resolve(value);
        }
        return null;
      });
    },
    [setDialog],
  );

  useEffect(() => {
    if (!dialog) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dialog, close]);

  const tone = dialog?.tone ?? "default";
  const confirmLabel = dialog?.confirmLabel ?? (dialog?.alert ? "OK" : "Confirm");
  const cancelLabel = dialog?.cancelLabel ?? "Cancel";

  const icon = useMemo(() => toneIcon(tone, dialog?.icon), [tone, dialog?.icon]);

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => close(false)}
        >
          <div
            className={cx(
              "w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-inset",
              toneRingClasses(tone),
            )}
            role={dialog.alert ? "alertdialog" : "dialog"}
            aria-modal="true"
            aria-labelledby="dialog-title"
            aria-describedby="dialog-desc"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-4 p-6">
              <div
                className={cx(
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 ring-inset",
                  tone === "danger" && "bg-rose-50 ring-rose-100",
                  tone === "warning" && "bg-amber-50 ring-amber-100",
                  tone === "success" && "bg-emerald-50 ring-emerald-100",
                  tone === "default" && "bg-blue-50 ring-blue-100",
                )}
              >
                {icon}
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="dialog-title"
                  className="text-base font-extrabold leading-snug text-slate-900"
                >
                  {dialog.title}
                </h2>
                {dialog.description && (
                  <p
                    id="dialog-desc"
                    className="mt-1 text-sm leading-relaxed text-slate-600"
                  >
                    {dialog.description}
                  </p>
                )}
                {dialog.content && (
                  <div id="dialog-desc" className="mt-3">
                    {dialog.content}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => close(false)}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label={cancelLabel}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-4">
              {!dialog.alert && (
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {cancelLabel}
                </button>
              )}
              <button
                type="button"
                onClick={() => close(true)}
                className={cx(
                  "rounded-xl px-4 py-2.5 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1",
                  toneButtonClasses(tone),
                )}
                autoFocus
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}
